import React, { useRef, useState, useEffect } from 'react';

/* ── Scroll visibility hook ─────────────────────────────────── */
export function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, vis };
}

/* ── Double-border wireframe box ────────────────────────────── */
export function WBox({ x, y, w, h, label, sub, accent, dim, small, mode = 'dark' }) {
  const cx = x + w / 2, cy = y + h / 2;
  let os, is, fb, tf;
  if (mode === 'light') {
    os = accent ? 'rgba(0,80,180,0.75)' : dim ? 'rgba(0,48,87,0.22)' : 'rgba(0,48,87,0.55)';
    is = accent ? 'rgba(0,80,180,0.2)'  : dim ? 'rgba(0,48,87,0.07)' : 'rgba(0,48,87,0.18)';
    fb = accent ? 'rgba(0,80,180,0.06)' : 'rgba(245,247,250,0.96)';
    tf = accent ? 'rgba(0,80,180,1)'    : dim ? 'rgba(0,48,87,0.38)' : '#131720';
  } else {
    os = accent ? 'rgba(100,165,230,0.85)' : dim ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.68)';
    is = accent ? 'rgba(100,165,230,0.28)' : dim ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.2)';
    fb = accent ? 'rgba(30,70,140,0.12)'   : 'rgba(10,12,18,0.96)';
    tf = accent ? 'rgba(130,190,255,1)'    : dim ? 'rgba(255,255,255,0.38)' : 'rgba(255,255,255,0.88)';
  }
  /* small=true: compact font for tight node boxes */
  const lfs = small ? '9'   : '11';
  const sfs = small ? '7.5' : '8.5';
  const lfw = accent ? (small ? '500' : '600') : (small ? '400' : '500');
  const ldy = small ? (sub ? cy - 4 : cy + 3.5) : (sub ? cy - 7 : cy + 4);
  const sdy = small ? cy + 7 : cy + 11;
  return (
    <g className="svg-box">
      <rect x={x} y={y} width={w} height={h} fill={fb} stroke={os} strokeWidth="1" />
      <rect x={x+4} y={y+4} width={w-8} height={h-8} fill="transparent" stroke={is} strokeWidth="0.75" />
      <text x={cx} y={ldy} textAnchor="middle" fill={tf}
        fontSize={lfs} fontFamily="'DM Mono','Courier New',monospace" fontWeight={lfw}>
        {label}
      </text>
      {sub && (
        <text x={cx} y={sdy} textAnchor="middle"
          fill={mode === 'light' ? 'rgba(0,48,87,0.38)' : 'rgba(255,255,255,0.35)'}
          fontSize={sfs} fontFamily="'DM Mono','Courier New',monospace">
          {sub}
        </text>
      )}
    </g>
  );
}

/* ── Large container frame ──────────────────────────────────── */
export function FBox({ x, y, w, h, label, accent, mode = 'dark' }) {
  let s, si, lbg, lt;
  if (mode === 'light') {
    s   = accent ? 'rgba(0,80,180,0.55)' : 'rgba(0,48,87,0.38)';
    si  = accent ? 'rgba(0,80,180,0.15)' : 'rgba(0,48,87,0.12)';
    lbg = '#f5f7fa';
    lt  = accent ? 'rgba(0,80,180,0.8)'  : 'rgba(0,48,87,0.65)';
  } else {
    s   = accent ? 'rgba(100,165,230,0.6)' : 'rgba(255,255,255,0.42)';
    si  = accent ? 'rgba(100,165,230,0.18)' : 'rgba(255,255,255,0.12)';
    lbg = '#0a0c12';
    lt  = 'rgba(255,255,255,0.58)';
  }
  const lw = label.length * 7.4 + 24;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill="transparent" stroke={s} strokeWidth="1" />
      <rect x={x+5} y={y+5} width={w-10} height={h-10} fill="transparent" stroke={si} strokeWidth="0.75" />
      <rect x={x+10} y={y-8} width={lw} height="16" fill={lbg} />
      <text x={x+17} y={y+3.5} fill={lt} fontSize="10"
        fontFamily="'DM Mono','Courier New',monospace" textAnchor="start">
        ▪ {label}
      </text>
    </g>
  );
}

/* ── Animated connector line ────────────────────────────────── */
export function CL({ x1, y1, x2, y2, delay = 0, dim = false, mode = 'dark' }) {
  const len = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  let color;
  if (mode === 'light') {
    color = dim ? 'rgba(0,48,87,0.20)' : 'rgba(0,48,87,0.45)';
  } else {
    color = dim ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.45)';
  }
  return (
    <line
      x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={color}
      strokeWidth="1"
      strokeDasharray={len}
      strokeDashoffset={len}
      className="conn-line"
      style={{ animationDelay: `${delay}s` }}
    />
  );
}

/* ── Junction dot ───────────────────────────────────────────── */
export function Dot({ cx, cy, accent = false, mode = 'dark' }) {
  let color;
  if (mode === 'light') {
    color = accent ? 'rgba(74,144,226,0.9)' : 'rgba(0,48,87,0.55)';
  } else {
    color = accent ? 'rgba(100,165,230,0.8)' : 'rgba(255,255,255,0.5)';
  }
  return (
    <circle cx={cx} cy={cy} r="2.5" fill={color} className="j-dot" />
  );
}

/* ── SVG background ─────────────────────────────────────────── */
export function BG({ id, w, h, mode = 'dark' }) {
  return (
    <rect width={w} height={h} fill="transparent" />
  );
}
